/**
 * SaldoDestinoChip — Chip que comunica el DESTINO del cobro pendiente.
 *
 * Complementa a EstadoChip (que ya muestra "Hold · debe $X" en amarillo):
 *   EstadoChip           → "hay saldo pendiente, cuánto"
 *   SaldoDestinoChip     → "dónde se cobra ese saldo"
 *
 * ── Cascada de decisión (alineada con MuellePage.jsx:139-146) ──
 * Muchas reservas históricas quedaron con `saldo_a_cargo_de` en NULL
 * aunque su `forma_pago_agencia` indicara claramente el destino. El módulo
 * de muelle ya resuelve esto internamente con una cascada de fallbacks; el
 * chip usa la misma para no discrepar de lo que el muelle muestra.
 *
 * Prioridad:
 *   1. `saldo_a_cargo_de` explícito  ('muelle' | 'agencia' | 'cliente')
 *   2. Split declarado:
 *        - `cxc_agencia_monto  > 0 && saldo_muelle_monto > 0`  → split (chip principal el mayor)
 *        - Solo `saldo_muelle_monto  > 0`  → muelle
 *        - Solo `cxc_agencia_monto  > 0`   → agencia
 *   3. `forma_pago_agencia`:
 *        - 'muelle'   → muelle
 *        - 'credito'  → CxC agencia
 *        - Otros métodos con saldo > 0 → INCONSISTENCIA (rojo)
 *          (efectivo / transferencia / tarjeta / bold_link / nequi / wompi
 *           no deberían dejar saldo pendiente — significa dato mal registrado)
 *   4. Sin nada de lo anterior → muelle (default del negocio)
 *
 * Devuelve null si `saldo_pendiente` es 0 o negativo — no ocupa espacio.
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import SailingIcon from '@mui/icons-material/Sailing';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const fmtCOP = (n) => '$' + new Intl.NumberFormat('es-CO',
  { minimumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const VARIANTES = {
  muelle: {
    label: 'Muelle',
    bg: '#fef3c7', color: '#92400e',
    Icon: SailingIcon,
    tooltip: 'Este saldo se cobra directamente al cliente en el muelle el día del pasadía.',
  },
  agencia: {
    label: 'CxC agencia',
    bg: '#ede9fe', color: '#5b21b6',
    Icon: AccountBalanceIcon,
    tooltip: 'Este saldo lo asume la agencia contra su cupo de crédito. Aparece en cuentas por cobrar.',
  },
  cliente: {
    label: 'Por cobrar',
    bg: '#e0f2fe', color: '#075985',
    Icon: PersonIcon,
    tooltip: 'Saldo pendiente por cobrar al cliente.',
  },
  inconsistente: {
    label: 'Saldo sin destino coherente',
    bg: '#fee2e2', color: '#991b1b',
    Icon: WarningAmberIcon,
    tooltip: 'Esta reserva tiene saldo pendiente pero la forma de pago no debería dejar saldo (efectivo, transferencia, tarjeta, etc.). Revisar el registro — puede ser un dato mal capturado.',
  },
};

// Formas de pago que NO deberían dejar saldo pendiente. Si hay saldo con
// una de estas, es señal de inconsistencia (bandera roja).
const FORMAS_SIN_SALDO = new Set([
  'efectivo', 'transferencia', 'tarjeta', 'datafono',
  'bold_link', 'nequi', 'wompi', 'epayco_link', 'cortesia',
]);

/**
 * Resuelve el destino del saldo aplicando la cascada.
 * @returns {'muelle'|'agencia'|'cliente'|'inconsistente'}
 */
function resolverDestino({ saldoACargoDe, saldoMuelleMonto, cxcAgenciaMonto, formaPagoAgencia }) {
  // 1. Splits declarados — van ANTES que el campo explícito.
  //
  // `saldo_a_cargo_de` solo admite un valor, así que es incapaz de describir
  // un saldo repartido: en ENT-20260908-010 dice 'muelle' mientras el saldo
  // son $420.000 de muelle MÁS $60.000 de CxC. Consultarlo primero hacía que
  // el chip nunca llegara aquí y anunciara los $480.000 completos como cobro
  // en muelle, mandando a cobrarle al turista lo que debe la agencia.
  const muMonto = Number(saldoMuelleMonto || 0);
  const cxMonto = Number(cxcAgenciaMonto || 0);
  if (muMonto > 0 && cxMonto > 0) return muMonto >= cxMonto ? 'muelle' : 'agencia';

  // 2. Explícito
  const explicito = (saldoACargoDe || '').toString().toLowerCase().trim();
  if (['muelle', 'agencia', 'cliente'].includes(explicito)) return explicito;

  if (muMonto > 0) return 'muelle';
  if (cxMonto > 0) return 'agencia';

  // 3. Derivar de forma_pago_agencia
  const fp = (formaPagoAgencia || '').toString().toLowerCase().trim();
  if (fp === 'muelle') return 'muelle';
  if (fp === 'credito') return 'agencia';
  if (fp === 'mixto') return 'muelle';  // los mixtos suelen dejar saldo al muelle
  if (fp && FORMAS_SIN_SALDO.has(fp)) return 'inconsistente';

  // 4. Default del negocio (mismo que MuellePage): sin especificar → muelle
  return 'muelle';
}

export default function SaldoDestinoChip({
  saldoPendiente,
  saldoACargoDe,
  saldoMuelleMonto,
  cxcAgenciaMonto,
  formaPagoAgencia,
  size = 'small',
}) {
  const saldo = Number(saldoPendiente || 0);
  if (saldo <= 0) return null;

  const muMonto = Number(saldoMuelleMonto || 0);
  const cxMonto = Number(cxcAgenciaMonto || 0);

  // Saldo repartido: dos cobranzas distintas, dos chips, cada uno con SU
  // cifra. Un solo chip con el total obliga a abrir la reserva para saber
  // cuánto se cobra dónde, que es justo lo que el chip existe para evitar.
  const tramos = (muMonto > 0 && cxMonto > 0)
    ? [{ destino: 'muelle', monto: muMonto }, { destino: 'agencia', monto: cxMonto }]
    : [{
      destino: resolverDestino({
        saldoACargoDe, saldoMuelleMonto, cxcAgenciaMonto, formaPagoAgencia,
      }),
      monto: saldo,
    }];

  const pintar = ({ destino, monto }) => {
    const cfg = VARIANTES[destino];
    return (
      <Tooltip key={destino} title={cfg.tooltip} arrow>
        <Chip
          size={size}
          icon={<cfg.Icon sx={{ fontSize: '14px !important',
                                color: `${cfg.color} !important` }} />}
          label={`${cfg.label} · ${fmtCOP(monto)}`}
          sx={{
            bgcolor: cfg.bg, color: cfg.color,
            fontWeight: 700, fontSize: 11, height: 22,
            '& .MuiChip-icon': { ml: 0.5 },
            '& .MuiChip-label': { pl: 0.5 },
          }}
        />
      </Tooltip>
    );
  };

  // Sin split se devuelve el chip suelto, no un fragmento: hay llamadores que
  // lo colocan dentro de un flex con `gap` y envolverlo cambiaría el espaciado
  // de las siete pantallas que ya lo usan.
  if (tramos.length === 1) return pintar(tramos[0]);
  return <>{tramos.map(pintar)}</>;
}
